import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import TabBar from "../TabBar";
import LanguageSwitcher from "../LanguageSwitcher";
import TableBadge from "../TableBadge";
import { SearchIcon, FilterIcon } from "../Icons";
import Input from "../Input";
import { useAppContext } from "../../context/AppContext";

/** The menu screen's header: restaurant, table, search with filter, category tabs. */
const Header = ({
  searchTerm,
  setSearchTerm,
  setHeaderHeight,
  filterOpen,
  onToggleFilter,
  filterPanel,
}) => {
  const { language, setLanguage, restaurantName } = useAppContext();
  const { t } = useTranslation();
  const headerRef = useRef(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el || !setHeaderHeight) return;
    const update = () => setHeaderHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [setHeaderHeight]);

  return (
    <div ref={headerRef} className="w-full flex flex-col bg-background header">
      <div className="flex justify-between items-start gap-3 px-5 pt-4">
        <div className="flex flex-col min-w-0">
          <h1 className="font-display text-title font-bold text-dark truncate">
            {restaurantName}
          </h1>
          <span className="text-sm text-muted">{t("menuGreeting")}</span>
        </div>
        <div className="flex items-start gap-2">
          <LanguageSwitcher setLanguage={setLanguage} language={language} />
          <TableBadge variant="square" />
        </div>
      </div>

      <div className="flex gap-3 px-5 pt-4">
        <label className="flex-1 flex items-center gap-2.5 h-14 px-4 rounded-lg bg-surface shadow-soft">
          <SearchIcon size={20} className="text-dark flex-none" />
          <Input
            type="search"
            name="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("Search items")}
            aria-label={t("Search items")}
            className="w-full h-full bg-transparent text-base font-medium text-dark placeholder:text-subtle focus:outline-none focus-visible:ring-0"
          />
        </label>
        <button
          type="button"
          onClick={onToggleFilter}
          aria-expanded={filterOpen ? "true" : "false"}
          aria-label={t("Filter")}
          className="w-14 h-14 flex-none rounded-lg bg-primary text-white shadow-brand flex items-center justify-center"
        >
          <FilterIcon size={22} />
        </button>
      </div>

      {filterPanel}

      <div className="px-5 pt-4 pb-2">
        <TabBar />
      </div>
    </div>
  );
};

export default Header;
